import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Card,
  CardBody,
  InputGroup,
  InputGroupText,
  Badge,
  Label,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PaymentReportService from "../../../services/OrderingServices/PaymentReportService";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import { Link, useNavigate } from "react-router-dom";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const DayBookLogs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentTypeList, setPaymentTypeList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Filters
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    createdBy: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Mobile Offcanvas
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  // Summary Stats (if needed in future)
  const [summaryStats] = useState({
    totalClosed: 0,
    totalAmount: 0,
  });

  // Format date helper
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getTimeOnly = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch Day Book Logs
  const fetchDayLog = async (
    page = 1,
    size = perPage,
    currentFilters = debouncedFilters
  ) => {
    if (!currentFilters.startDate || !currentFilters.endDate) return;

    setLoading(true);
    try {
      const response = await PaymentReportService.dayBookLog(
        currentFilters.startDate,
        currentFilters.endDate
      );

      const logs = response?.items || [];
      setPaymentTypeList(logs);
      setTotalRows(logs.length); // Adjust if backend supports pagination
    } catch (error) {
      console.error("Error fetching day book logs:", error);
      toast.error("Failed to fetch day book logs");
      setPaymentTypeList([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounce filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  // Trigger fetch on debounced filters change
  useEffect(() => {
    if (debouncedFilters.startDate && debouncedFilters.endDate) {
      fetchDayLog(currentPage, perPage, debouncedFilters);
    }
  }, [debouncedFilters, currentPage, perPage]);

  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1); // 1 month back

    const formatLocal = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setFilters((prev) => ({
      ...prev,
      startDate: formatLocal(today),
      endDate: formatLocal(today),
    }));
  }, []);

  // Unique CreatedBy for dropdown
  const uniqueCreatedBy = [
    ...new Set(paymentTypeList.map((item) => item.createdBy)),
  ];

  // Client-side filtering by createdBy (since API doesn't support it)
  const filteredData = paymentTypeList
    .filter((item) =>
      filters.createdBy
        ? item.createdBy.toLowerCase() === filters.createdBy.toLowerCase()
        : true
    )
    .slice((currentPage - 1) * perPage, currentPage * perPage);

  // For mobile card serial number
  const getSerialNumber = (index) => (currentPage - 1) * perPage + index + 1;

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => getSerialNumber(index),
      width: "80px",
      center: true,
    },
    {
      name: "Closed By",
      selector: (row) => row.createdBy,
      sortable: true,
      sortField: "createdBy",
      wrap: true,
    },
    {
      name: "Closed Date",
      selector: (row) => formatDisplayDate(row.createdAt),
      center: true,
      sortable: true,
      sortField: "createdAt",
    },
    {
      name: "Closed Time",
      selector: (row) => getTimeOnly(row.createdAt),
      center: true,
    },
    {
      name: "Total Amount",
      selector: (row) => row.totalAmount,
      cell: (row) => <NumberWithCommas number={row.totalAmount} />,
      right: true,
      sortable: true,
      sortField: "totalAmount",
    },
    {
      name: "Action",
      cell: (row) => (
        <Button
          color="soft-success"
          size="sm"
          onClick={() => navigate(`/logs/day-book/${row.id}`)}
          className="btn-icon"
        >
          <i className="bx bx-show fs-5" />
        </Button>
      ),
      center: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
  ];

  const SummaryCard = ({ title, value, color, icon }) => (
    <Card className="shadow-sm border-0">
      <CardBody className="d-flex align-items-center justify-content-between">
        <div>
          <h6 className="fw-semibold text-uppercase" style={{ color }}>
            {title}
          </h6>
          <h4 className="fs-2 fw-bold" style={{ color }}>
            <NumberWithCommas number={value} />
          </h4>
        </div>
        <div
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: color + "20",
            color,
            width: "48px",
            height: "48px",
          }}
        >
          <i className={`${icon} fs-4`} />
        </div>
      </CardBody>
    </Card>
  );

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Day Book Logs" pageTitle="Logs" />

        {/* Mobile Filter Button */}
        <div className="d-lg-none d-block mb-3">
          <Button color="success" onClick={() => setShowOffcanvas(true)}>
            <i className="bx bx-filter-alt me-1"></i> Filters
          </Button>
        </div>

        {/* Mobile Offcanvas */}
        <Offcanvas
          isOpen={showOffcanvas}
          toggle={() => setShowOffcanvas(false)}
          direction="bottom"
          className="d-lg-none"
          style={{
            height: "80vh",
            borderTopLeftRadius: "1rem",
            borderTopRightRadius: "1rem",
          }}
        >
          <OffcanvasHeader toggle={() => setShowOffcanvas(false)}>
            <h5>Filter Day Book Logs</h5>
          </OffcanvasHeader>
          <OffcanvasBody>
            <div className="mb-3">
              <Label>Date Range</Label>
              <Flatpickr
                className="form-control"
                value={[filters.startDate, filters.endDate]}
                options={{ mode: "range", dateFormat: "Y-m-d" }}
                onChange={([start, end]) => {
                  if (start && end) {
                    setFilters((prev) => ({
                      ...prev,
                      startDate: formatLocalDate(start),
                      endDate: formatLocalDate(end),
                    }));
                  }
                }}
                placeholder="Select date range"
              />
            </div>
            <div className="mb-3">
              <Label>Closed By</Label>
              <Input
                type="select"
                value={filters.createdBy}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, createdBy: e.target.value }))
                }
              >
                <option value="">All Users</option>
                {uniqueCreatedBy.map((user) => (
                  <option key={user} value={user}>
                    {user}
                  </option>
                ))}
              </Input>
            </div>
          </OffcanvasBody>
          <div className="p-3 border-top bg-white">
            <Button
              color="success"
              block
              onClick={() => setShowOffcanvas(false)}
            >
              Apply Filters
            </Button>
          </div>
        </Offcanvas>

        {/* Desktop View */}
        <div
          className="d-lg-block d-none p-3 p-lg-4 bg-white rounded shadow-sm border"
          style={{ border: "1px solid #eee" }}
        >
          <Row className="mb-3 align-items-center">
            <Col md={6}>
              <h5 className="mb-1 fw-bold text-dark">Day Book Logs</h5>
              {/* <p className="text-muted small mb-0">
                Showing entries from {filters.startDate} to {filters.endDate}
              </p> */}

              {/* {filters.startDate === new Date().toISOString().split("T")[0] ? (
                // <p className="text-muted small mb-0">Today's Day Book Logs</p>
              ) : (
                <p className="text-muted small mb-0">
                  Showing {filters.startDate} to {filters.endDate}
                </p>
              )} */}
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-end gap-2">
                <InputGroup style={{ maxWidth: "300px" }}>
                  <Input
                    type="select"
                    value={filters.createdBy}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        createdBy: e.target.value,
                      }))
                    }
                  >
                    <option value="">All Users</option>
                    {uniqueCreatedBy.map((user) => (
                      <option key={user} value={user}>
                        {user}
                      </option>
                    ))}
                  </Input>
                  <InputGroupText>
                    <i className="bx bx-user" />
                  </InputGroupText>
                </InputGroup>

                <InputGroup style={{ maxWidth: "300px" }}>
                  {/* <Flatpickr
                    className="form-control"
                    value={`${filters.startDate} to ${filters.endDate}`}
                    options={{ mode: "range", dateFormat: "Y-m-d" }}
                    onChange={([start, end]) => {
                      if (start && end) {
                        setFilters((prev) => ({
                          ...prev,
                          startDate: formatLocalDate(start),
                          endDate: formatLocalDate(end),
                        }));
                      }
                    }}
                    placeholder="Select Date Range"
                  /> */}

                  <Flatpickr
                    value={
                      filters.startDate && filters.endDate
                        ? `${filters.startDate} to ${filters.endDate}`
                        : ""
                    }
                    options={{
                      mode: "range",
                      dateFormat: "Y-m-d",
                    }}
                    onChange={(dates) => {
                      if (dates.length === 2) {
                        const start = formatLocalDate(dates[0]);
                        const end = formatLocalDate(dates[1]);
                        setFilters((prev) => ({
                          ...prev,
                          startDate: start,
                          endDate: end,
                        }));
                      }
                    }}
                    placeholder="Select Date Range"
                    className="form-control"
                  />
                  <InputGroupText>
                    <i className="bx bx-calendar" />
                  </InputGroupText>
                </InputGroup>
              </div>
            </Col>
          </Row>

          <hr className="my-3" />

          <CustomDataTable
            columns={columns}
            data={filteredData} // can be empty array
            noHeader
            pagination={filteredData?.length > 0}
            striped
            highlightOnHover
            responsive
            persistTableHead
            noDataComponent={
              <div className="text-center my-5 w-100">
                <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                <h5 className="fw-semibold text-secondary">
                  No daybook logs found.
                </h5>
                {/* <p className="text-muted mb-0">
                  {filters.createdBy
                    ? "No transactions found for the selected payment type and date range."
                    : "Please choose a payment type to see updated availability."}
                </p> */}
              </div>
            }
          />
        </div>

        {/* Mobile Cards View */}
        <div className="d-block d-lg-none">
          {loading ? (
            <div className="text-center py-5">
              <Triangle height="60" width="60" color="#5B71B9" />
            </div>
          ) : filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <Card key={item.id} className="mb-3 shadow-sm">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1 fw-bold">{item.createdBy}</h6>
                      <small className="text-muted">
                        {formatDisplayDate(item.createdAt)} •{" "}
                        {getTimeOnly(item.createdAt)}
                      </small>
                      <div className="mt-2">
                        <Badge color="light" className="me-2">
                          S.N: {getSerialNumber(index)}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-end">
                      <h5 className="text-success fw-bold">
                        Rs.{" "}
                        <NumberWithCommas
                          number={item.totalAmount.toFixed(2)}
                        />
                      </h5>
                      <Button
                        color="soft-success"
                        size="sm"
                        className="mt-2"
                        onClick={() => navigate(`/logs/day-book/${item.id}`)}
                      >
                        <i className="bx bx-show" /> View
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))
          ) : (
            <Card className="text-center py-5 text-muted">
              <CardBody>No day book logs found for selected filters.</CardBody>
            </Card>
          )}
        </div>
      </div>
    </Container>
  );
};

export default DayBookLogs;
